/* REFORMED — global.js
   Announcement rotation (this file grows with the global layer:
   header scroll state, popup). Mockup parity: see <script> blocks
   in the approved HTML files. */

(function () {
  'use strict';

  /* ---------- Announcement bar rotation ---------- */
  function initAnnounce(track) {
    var msgs = track.querySelectorAll('.announce-msg');
    if (track._announceTimer) {
      clearInterval(track._announceTimer);
      track._announceTimer = null;
    }
    if (msgs.length < 2) return;

    /* screen readers hear only the visible message, not all three layers */
    msgs.forEach(function (m) {
      m.setAttribute('aria-hidden', m.classList.contains('active') ? 'false' : 'true');
    });

    var interval = parseInt(track.dataset.interval, 10) || 4000;
    var i = 0;
    track._announceTimer = setInterval(function () {
      msgs[i].classList.remove('active');
      msgs[i].setAttribute('aria-hidden', 'true');
      i = (i + 1) % msgs.length;
      msgs[i].classList.add('active');
      msgs[i].setAttribute('aria-hidden', 'false');
    }, interval);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-announce]').forEach(initAnnounce);
  }

  initAll();

  /* ---------- Announcement height → CSS var ----------
     Keeps the overlay header's resting offset correct if the bar is
     removed/resized in the customizer (default 38px in base.css). */
  function syncAnnounceHeight() {
    var bar = document.querySelector('.announce');
    document.documentElement.style.setProperty('--announce-h', (bar ? bar.offsetHeight : 0) + 'px');
  }
  syncAnnounceHeight();
  window.addEventListener('resize', syncAnnounceHeight);

  /* ---------- Interior header: sticky wrapper fallback ----------
     base.css pins the section wrapper via .header-section:has(.header--sticky);
     this covers browsers without :has() support. */
  function initStickyFallback() {
    var supportsHas = false;
    try {
      supportsHas = CSS.supports('selector(:has(*))');
    } catch (err) { /* very old browser */ }
    if (supportsHas) return;
    var sticky = document.querySelector('.header--sticky');
    if (sticky) {
      var wrapper = sticky.closest('.shopify-section');
      if (wrapper) wrapper.classList.add('is-sticky');
    }
  }
  initStickyFallback();

  /* ---------- Header: transparent → solid on scroll (homepage) ----------
     Mockup parity: header gains .solid past the announcement bar height. */
  function initHeaderScroll() {
    var header = document.querySelector('[data-header-overlay]');
    if (!header) return;
    var onScroll = function () {
      var bar = document.querySelector('.announce');
      var threshold = bar ? bar.offsetHeight : 38;
      header.classList.toggle('solid', window.scrollY > threshold);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }
  initHeaderScroll();

  /* ---------- Nav drawer ---------- */
  var pageOverlay = document.querySelector('[data-page-overlay]');

  /* The drawer is authored inside the header section, but the sticky header
     wrapper has z-index:60 — a stacking context that would trap the drawer
     (z-index:100) BELOW the page overlay (z-index:90) on interior templates.
     Move it to <body> so it layers in the root context, exactly like the
     cart drawer. On theme-editor section reloads, the fresh copy replaces
     any previously adopted one. */
  function adoptNavDrawer() {
    var fresh = document.querySelector('.shopify-section [data-nav-drawer]');
    if (!fresh) return;
    document.querySelectorAll('body > [data-nav-drawer]').forEach(function (old) {
      old.remove();
    });
    document.body.appendChild(fresh);
  }
  adoptNavDrawer();

  function navDrawer() {
    return document.querySelector('[data-nav-drawer]');
  }
  function navButton() {
    return document.querySelector('[data-nav-open]');
  }
  function openNav() {
    var drawer = navDrawer();
    if (!drawer) return;
    drawer.classList.add('open');
    if (pageOverlay) pageOverlay.classList.add('open');
    var btn = navButton();
    if (btn) btn.setAttribute('aria-expanded', 'true');
    var close = drawer.querySelector('[data-nav-close]');
    if (close) close.focus();
  }
  function closeNav() {
    var drawer = navDrawer();
    if (!drawer) return;
    drawer.classList.remove('open');
    if (pageOverlay) pageOverlay.classList.remove('open');
    var btn = navButton();
    if (btn) {
      btn.setAttribute('aria-expanded', 'false');
      btn.focus();
    }
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-nav-open]')) openNav();
    if (e.target.closest('[data-nav-close]')) closeNav();
    if (e.target.closest('[data-page-overlay]')) closeNav();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeNav();
  });

  /* ---------- Card hover images: hydrate on hover-capable devices only ----------
     Phones never fire the hover swap, so don't make them download it. */
  function initHoverImages(scope) {
    if (!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches)) return;
    (scope || document).querySelectorAll('img.card-hover-img[data-hover-src]').forEach(function (img) {
      img.src = img.dataset.hoverSrc;
      if (img.dataset.hoverSrcset) img.srcset = img.dataset.hoverSrcset;
      img.removeAttribute('data-hover-src');
    });
  }
  initHoverImages();

  /* ---------- Scroll reveal ----------
     One shared observer; CSS hides [data-reveal] only under html.js, and
     prefers-reduced-motion forces everything visible — nothing can stay
     hidden without JS or with motion off. */
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var revealObserver = null;
  function initReveal(scope) {
    var targets = (scope || document).querySelectorAll('[data-reveal]:not(.in-view)');
    if (!targets.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('in-view'); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px' });
    }
    targets.forEach(function (el) { revealObserver.observe(el); });
  }
  initReveal();

  /* ---------- Stat count-up ----------
     Parses "38k+" style values: numeric prefix animates, suffix rides along. */
  function initCountUp() {
    var stats = document.querySelectorAll('[data-count]');
    if (!stats.length) return;
    function run(el) {
      var raw = el.dataset.count || '';
      var match = raw.match(/^([\d.,]+)(.*)$/);
      if (!match) return;
      var target = parseFloat(match[1].replace(/,/g, ''));
      var suffix = match[2] || '';
      var hasComma = match[1].indexOf(',') > -1;
      if (isNaN(target)) return;
      var start = null, duration = 1200;
      function frame(ts) {
        if (!start) start = ts;
        var p = Math.min(1, (ts - start) / duration);
        var eased = 1 - Math.pow(1 - p, 3); /* easeOutCubic */
        var value = Math.round(target * eased);
        el.textContent = (hasComma ? value.toLocaleString('en-US') : value) + suffix;
        if (p < 1) requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }
    if (reduceMotion || !('IntersectionObserver' in window)) return; /* server-rendered value stays */
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          run(entry.target);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.6 });
    stats.forEach(function (el) { io.observe(el); });
  }
  initCountUp();

  /* ---------- Accordions (PDP buy box + image-accordion sections) ----------
     Mockup parity: max-height ease, + rotates. Bind guard allows safe
     re-init from theme-editor section loads. */
  function initAccordions(scope) {
    (scope || document).querySelectorAll('[data-accordion]').forEach(function (acc) {
      if (acc._accBound) return;
      acc._accBound = true;
      var body = acc.querySelector('.acc-body');
      var head = acc.querySelector('[data-accordion-toggle]');
      if (!body || !head) return;
      if (acc.classList.contains('open')) body.style.maxHeight = body.scrollHeight + 'px';
      head.addEventListener('click', function () {
        var open = acc.classList.toggle('open');
        head.setAttribute('aria-expanded', open ? 'true' : 'false');
        body.style.maxHeight = open ? body.scrollHeight + 'px' : 0;
      });
    });
  }
  initAccordions();
  /* keep open accordion heights accurate when the viewport reflows */
  window.addEventListener('resize', function () {
    document.querySelectorAll('[data-accordion].open .acc-body').forEach(function (body) {
      body.style.maxHeight = body.scrollHeight + 'px';
    });
  });

  /* ---------- Drop strip countdown ---------- */
  function initDropCountdown(scope) {
    (scope || document).querySelectorAll('[data-drop-countdown]').forEach(function (el) {
      if (el._dropTimer) clearInterval(el._dropTimer);
      var end = new Date(el.dataset.end.trim().replace(' ', 'T'));
      if (isNaN(end.getTime())) return; /* unparseable — stay hidden */
      function pad(n) { return String(n).padStart(2, '0'); }
      function tick() {
        var ms = end - Date.now();
        if (ms <= 0) {
          el.textContent = 'Live Now';
          clearInterval(el._dropTimer);
          return;
        }
        var d = Math.floor(ms / 86400000);
        var h = Math.floor(ms / 3600000) % 24;
        var m = Math.floor(ms / 60000) % 60;
        var s = Math.floor(ms / 1000) % 60;
        el.textContent = (d > 0 ? d + 'd ' : '') + pad(h) + ':' + pad(m) + ':' + pad(s);
      }
      el.hidden = false;
      tick();
      el._dropTimer = setInterval(tick, 1000);
    });
  }
  initDropCountdown();

  /* ---------- Collection sort: submit on change (native ?sort_by=) ---------- */
  document.addEventListener('change', function (e) {
    if (e.target.matches('[data-sort-select]')) {
      var form = e.target.closest('[data-sort-form]');
      if (form) form.submit();
    }
  });

  /* ---------- Hero video: robust autoplay + silent poster fallback ----------
     Mobile autoplay is the unreliable case (iOS Low Power Mode, Data
     Saver, slow cells). Contract:
     - poster overlays the video; the video is revealed ('playing' event
       → .video-live on .vhero) so a stalled/blocked video never shows a
       paused frame or the native start-playback glyph.
     - muted is re-asserted as a PROPERTY before every play(); play() is
       (re)tried on loadeddata/canplay and its rejection is caught.
     - on rejection: retry ONCE on the first user interaction
       (touch/scroll/click — touchend carries user activation, touchstart
       does not). If that also fails, settle on the poster for the
       session — silently, never surfacing controls.
     - prefers-reduced-motion: poster only, no autoplay (Brief §9);
       pausing at frame 0 matches the poster (extracted from frame 0). */
  function initHeroVideo(root) {
    var videos = (root || document).querySelectorAll('[data-hero-video]');
    if (!videos.length) return;
    var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    function reduced() { return mq ? mq.matches : false; }

    videos.forEach(function (video) {
      var hero = video.closest('.vhero');
      var retryArmed = false, retryUsed = false, settled = false;
      var interactionEvents = ['touchend', 'scroll', 'click'];

      video.removeAttribute('controls'); // belt & braces — native UI never
      video.defaultMuted = true;

      function reveal() { if (hero) hero.classList.add('video-live'); }
      function toPoster() { if (hero) hero.classList.remove('video-live'); }

      function onFirstInteraction() {
        if (retryUsed || settled) return;
        retryUsed = true;
        interactionEvents.forEach(function (t) {
          document.removeEventListener(t, onFirstInteraction, true);
        });
        if (reduced()) return;
        video.muted = true;
        var p = video.play();
        if (p && p.catch) p.catch(function () { settled = true; }); // poster is the hero now
      }

      function onReject() {
        if (settled || reduced()) return;
        if (retryUsed) { settled = true; return; }
        if (retryArmed) return;
        retryArmed = true;
        interactionEvents.forEach(function (t) {
          document.addEventListener(t, onFirstInteraction, { once: true, passive: true, capture: true });
        });
      }

      function tryPlay() {
        if (settled || reduced()) return;
        if (!video.paused && !video.ended) return; // already rolling
        video.muted = true; // property, BEFORE play() — required for autoplay
        var p = video.play();
        if (p && p.catch) p.catch(onReject);
      }

      video.addEventListener('playing', reveal);
      video.addEventListener('loadeddata', tryPlay);
      video.addEventListener('canplay', tryPlay);

      function applyMotionPref() {
        if (reduced()) {
          toPoster();
          video.pause();
          try { video.currentTime = 0; } catch (err) { /* not loaded yet */ }
        } else {
          tryPlay();
        }
      }
      if (mq && mq.addEventListener) mq.addEventListener('change', applyMotionPref);
      applyMotionPref();

      /* the autoplay attribute can win the race before listeners attach */
      if (!reduced() && !video.paused && !video.ended && video.readyState >= 3) reveal();
    });
  }
  initHeroVideo();

  /* Theme editor: re-init when the merchant edits a section */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
    syncAnnounceHeight();
    initHeaderScroll();
    adoptNavDrawer();
    initDropCountdown(e.target);
    initAccordions(e.target);
    initReveal(e.target);
    initHoverImages(e.target);
    initHeroVideo(e.target);
  });
})();
